import { Checkbox, CheckboxProps, Flex } from "antd";
import { FC, useCallback, useMemo } from "react";
import {
    Market,
    SubtaskFinGroupPhotos,
    TaskAssortmentMatrix,
} from "tm-shared/types";

import { ForceStepComplete } from "@/features/ForceStepComplete";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { setAssortmentMatrixSubtaskPayload } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";

import {
    getNextSubtaskId,
    makeForceStepCompletePayload,
} from "../../../AssortmentMatrix.utils";

interface FinGroupPhotosControllerProps {
    subtask: SubtaskFinGroupPhotos;
    market: Market;
    task: TaskAssortmentMatrix;
}

export const FinGroupPhotosController: FC<FinGroupPhotosControllerProps> = ({
    subtask,
    market,
    task,
}) => {
    const dispatch = useAppDispatch();
    const marketId = market.id;
    const taskId = task.id;
    const subtaskId = subtask.subtaskId;

    const tasksConfig = useAppSelector((state) => state.ui.mdTasksConfig);

    // Конфігурація поточної підзадачі
    const currentSubtaskConfig = useMemo(
        () => tasksConfig?.[marketId]?.[taskId]?.subtasks?.[subtaskId],
        [tasksConfig, marketId, taskId, subtaskId],
    );

    const shouldShowUncompleted = useMemo(
        () => currentSubtaskConfig?.showUncompleted ?? false,
        [currentSubtaskConfig?.showUncompleted],
    );

    const onChange: CheckboxProps["onChange"] = (e) => {
        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                subtaskId,
                field: "showUncompleted",
                value: e.target.checked,
            }),
        );

        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                subtaskId,
                value: null,
            }),
        );
    };

    const handleForceStepComplete = useCallback(
        async (comment: string) => {
            const payload = makeForceStepCompletePayload({
                subtask,
                comment,
            });

            dispatch(
                setAssortmentMatrixSubtaskPayload({
                    taskId,
                    subtaskId,
                    payload,
                }),
            );

            const { nextSubtaskId } = getNextSubtaskId({
                subtasks: task.context.subtasks,
                currentSubtaskId: subtaskId,
            });

            if (nextSubtaskId) {
                dispatch(
                    setMdTasksConfig({
                        marketId,
                        taskId,
                        field: "activeItem",
                        value: nextSubtaskId,
                    }),
                );
            }
        },
        [dispatch, marketId, subtask, subtaskId, task.context.subtasks, taskId],
    );

    return (
        <Flex gap={16} align="center" justify="space-between">
            <Checkbox onChange={onChange} checked={shouldShowUncompleted}>
                Показати невиконані
            </Checkbox>

            <ForceStepComplete
                onComplete={handleForceStepComplete}
                taskName={task.name}
                marketName={market.name}
                entity="step"
            />
        </Flex>
    );
};
